<template>
  <b-modal
    :model-value="visible"
    title="Workflow hooks"
    size="lg"
    hide-footer
    @update:model-value="$emit('update:visible', $event)"
  >
    <template #title>
      Workflow hooks
      <modal-help topic="spreadsheet-workflow-hooks" />
    </template>

    <p class="hook-note">
      A hook runs a step workflow when a record on this spreadsheet is created, edited, or deleted.
      The saved record is the starting record, or you can start from a record related to it.
      To run a workflow yourself, check the rows and use bulk actions.
    </p>

    <div v-if="loading" class="hook-empty">Loading hooks…</div>
    <div v-else>
      <p v-if="!workflows.length" class="hook-empty">
        Create a step workflow first, then come back here to hook it up.
      </p>
      <div v-for="(hook, index) in hooks" :key="hook.id" class="hook-card">
        <div class="hook-row">
          <select v-model="hook.event" class="hook-input" aria-label="When the hook runs" @change="onEventChange(hook)">
            <option value="create">When a record is created</option>
            <option value="update">When a record is edited</option>
            <option value="delete">When a record is deleted</option>
          </select>
          <select v-model="hook.recordSource" class="hook-input" aria-label="Starting record" @change="onRecordSourceChange(hook)">
            <option value="record">This record</option>
            <option value="related">A related record</option>
          </select>
          <button type="button" class="hook-remove" @click="removeHook(index)">Remove</button>
        </div>
        <div v-if="hook.recordSource === 'related'" class="hook-row">
          <select :value="relationshipKey(hook)" class="hook-input hook-input-wide" aria-label="Relationship" @change="setRelationship(hook, $event.target.value)">
            <option value="">Relationship…</option>
            <option v-for="target in relationshipTargets" :key="target.key" :value="target.key">{{ target.label }}</option>
          </select>
        </div>
        <div class="hook-row">
          <select v-model="hook.workflowUniqueTitle" class="hook-input hook-input-wide" aria-label="Step workflow" @change="loadLatest(hook.workflowUniqueTitle)">
            <option value="">Choose a workflow</option>
            <option v-for="workflow in workflowsFor(hook)" :key="workflow.uniqueTitle" :value="workflow.uniqueTitle">
              {{ workflow.title }}
            </option>
          </select>
        </div>
        <div v-if="hook.event === 'update'" class="hook-block">
          <label class="hook-label">
            Only when these fields change
            <select v-model="hook.changedFields" class="hook-fields" multiple aria-label="Fields that must change">
              <option v-for="column in changeableColumns" :key="column.uniqueTitle" :value="column.uniqueTitle">
                {{ column.title || column.uniqueTitle }}
              </option>
            </select>
          </label>
          <span class="hook-hint">Leave this empty to run on every edit.</span>
        </div>
        <runtime-filter-builder
          :key="hook.id"
          :field-groups="fieldGroups"
          :initial-filters="hook.filters || []"
          :initial-match-mode="hook.filterMatchMode || 'all'"
          @filter-changed="onFilters(hook, $event)"
        />
        <p v-if="warningFor(hook)" class="hook-warning">{{ warningFor(hook) }}</p>
        <p v-if="latestFor(hook)" class="hook-latest">Last run: {{ latestFor(hook) }}</p>
      </div>
      <div class="hook-actions">
        <button type="button" class="hook-add" :disabled="!workflows.length" @click="addHook">Add hook</button>
        <button type="button" class="hook-save" :disabled="saving" @click="save">
          {{ saving ? 'Saving…' : 'Save hooks' }}
        </button>
        <span v-if="saveMessage" class="hook-saved">{{ saveMessage }}</span>
        <span v-if="saveError" class="hook-error">{{ saveError }}</span>
      </div>
    </div>
  </b-modal>
</template>

<script>
import RuntimeFilterBuilder from './RuntimeFilterBuilder.vue'
import { relationshipWriteTargets } from '../../../shared/submission-record-actions.js'

export default {
  components: { RuntimeFilterBuilder },
  props: {
    visible: { type: Boolean, default: false },
    spreadSheetUniqueTitle: { type: String, default: '' },
    applicationUniqueTitle: { type: String, default: '' },
    spreadSheet: { type: Object, default: null },
    spreadsheets: { type: Array, default: () => [] },
    fieldGroups: { type: Array, default: () => [] }
  },
  emits: ['update:visible'],
  data () {
    return {
      loading: false,
      saving: false,
      hooks: [],
      workflows: [],
      overlaps: {},
      latest: {},
      saveMessage: '',
      saveError: ''
    }
  },
  computed: {
    relationshipTargets () {
      return relationshipWriteTargets(this.spreadSheet, this.spreadsheets)
    },
    changeableColumns () {
      return ((this.spreadSheet && this.spreadSheet.columns) || []).filter((column) => {
        return column && column.type !== 'formula' && column.type !== 'password'
      })
    }
  },
  watch: {
    visible (value) {
      if (value) this.load()
    }
  },
  methods: {
    headers () {
      return { 'application-unique-title': this.applicationUniqueTitle }
    },
    workflowsFor (hook) {
      const sheet = hook.recordSource === 'related' ? this.targetSheet(hook) : this.spreadSheetUniqueTitle
      return (this.workflows || []).filter((workflow) => workflow && workflow.spreadSheetUniqueTitle === sheet)
    },
    targetSheet (hook) {
      const match = this.relationshipTargets.find((target) => target.key === this.relationshipKey(hook))
      return match ? match.relatedSpreadSheet : ''
    },
    relationshipKey (hook) {
      if (hook.relatedSource === 'reverse') return `reverse:${hook.sourceSpreadsheet}:${hook.sourceRelationshipColumn}`
      return hook.relationshipColumn ? `forward:${hook.relationshipColumn}` : ''
    },
    warningFor (hook) {
      if (!hook || hook.event === 'delete' || !hook.workflowUniqueTitle) return ''
      const entry = this.overlaps[hook.workflowUniqueTitle]
      return entry && entry.hookWarning ? entry.hookWarning : ''
    },
    latestFor (hook) {
      const run = hook && this.latest[hook.workflowUniqueTitle]
      if (!run) return ''
      const when = run.finishedAt ? new Date(run.finishedAt).toLocaleString() : ''
      const detail = run.message ? ` — ${run.message}` : ''
      return `${run.status} ${when}${detail}`
    },
    async load () {
      this.loading = true
      this.saveMessage = ''
      this.saveError = ''
      try {
        const [sheet, workflows, overlaps] = await Promise.all([
          this.$http.get(
            `${VUE_APP_PROXY_SERVER_URL}/spreadsheets/${this.spreadSheetUniqueTitle}`,
            { headers: this.headers() }
          ),
          this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/step-workflows/`, { headers: this.headers() }),
          this.$http.get(
            `${VUE_APP_PROXY_SERVER_URL}/spreadsheets/${this.spreadSheetUniqueTitle}/workflow-overlaps`,
            { headers: this.headers() }
          )
        ])
        const stored = sheet.data && Array.isArray(sheet.data.workflowHooks) ? sheet.data.workflowHooks : []
        this.hooks = stored.map((hook, index) => this.blankHook(hook, index))
        this.workflows = workflows.data || []
        this.overlaps = overlaps.data || {}
        await Promise.all(this.hooks.map((hook) => this.loadLatest(hook.workflowUniqueTitle)))
      } catch (error) {
        this.saveError = 'Could not load hooks.'
        this.hooks = []
        this.workflows = []
      } finally {
        this.loading = false
      }
    },
    blankHook (hook, index) {
      const source = hook || {}
      return {
        id: source.id || `hook_${index + 1}`,
        event: source.event === 'update' || source.event === 'delete' ? source.event : 'create',
        workflowUniqueTitle: source.workflowUniqueTitle || '',
        recordSource: source.recordSource === 'related' ? 'related' : 'record',
        relatedSource: source.relatedSource === 'reverse' ? 'reverse' : 'forward',
        relationshipColumn: source.relationshipColumn || '',
        sourceSpreadsheet: source.sourceSpreadsheet || '',
        sourceRelationshipColumn: source.sourceRelationshipColumn || '',
        filters: source.filters || [],
        filterMatchMode: source.filterMatchMode === 'any' ? 'any' : 'all',
        changedFields: Array.isArray(source.changedFields) ? source.changedFields.slice() : []
      }
    },
    addHook () {
      const first = (this.workflows || []).find((workflow) => workflow.spreadSheetUniqueTitle === this.spreadSheetUniqueTitle)
      this.hooks.push(this.blankHook({
        id: `hook_${Date.now()}`,
        workflowUniqueTitle: first ? first.uniqueTitle : ''
      }, this.hooks.length))
      this.saveMessage = ''
    },
    removeHook (index) {
      this.hooks.splice(index, 1)
      this.saveMessage = ''
    },
    onEventChange (hook) {
      if (hook.event !== 'update') hook.changedFields = []
    },
    onRecordSourceChange (hook) {
      if (hook.recordSource !== 'related') {
        hook.relationshipColumn = ''
        hook.sourceSpreadsheet = ''
        hook.sourceRelationshipColumn = ''
      }
      const options = this.workflowsFor(hook)
      if (!options.some((workflow) => workflow.uniqueTitle === hook.workflowUniqueTitle)) {
        hook.workflowUniqueTitle = options[0] ? options[0].uniqueTitle : ''
      }
    },
    setRelationship (hook, key) {
      const match = this.relationshipTargets.find((target) => target.key === key)
      if (!match) return
      hook.relatedSource = match.relatedSource
      hook.relationshipColumn = match.relationshipColumn || ''
      hook.sourceSpreadsheet = match.sourceSpreadsheet || ''
      hook.sourceRelationshipColumn = match.sourceRelationshipColumn || ''
      this.onRecordSourceChange(hook)
    },
    onFilters (hook, event) {
      hook.filters = (event && event.filters) || []
      hook.filterMatchMode = event && event.matchMode === 'any' ? 'any' : 'all'
    },
    async loadLatest (workflowUniqueTitle) {
      if (!workflowUniqueTitle || this.latest[workflowUniqueTitle]) return
      try {
        const response = await this.$http.get(
          `${VUE_APP_PROXY_SERVER_URL}/step-workflows/${workflowUniqueTitle}/logs`,
          { headers: this.headers() }
        )
        const rows = response.data || []
        this.latest = Object.assign({}, this.latest, { [workflowUniqueTitle]: rows[0] || null })
      } catch (error) {
        this.latest = Object.assign({}, this.latest, { [workflowUniqueTitle]: null })
      }
    },
    async save () {
      this.saving = true
      this.saveMessage = ''
      this.saveError = ''
      try {
        const response = await this.$http.put(
          `${VUE_APP_PROXY_SERVER_URL}/spreadsheets/${this.spreadSheetUniqueTitle}/workflow-hooks`,
          { hooks: this.hooks.filter((hook) => hook.workflowUniqueTitle) },
          { headers: this.headers() }
        )
        const stored = response.data && Array.isArray(response.data.workflowHooks) ? response.data.workflowHooks : []
        this.hooks = stored.map((hook, index) => this.blankHook(hook, index))
        this.saveMessage = 'Saved'
        const overlaps = await this.$http.get(
          `${VUE_APP_PROXY_SERVER_URL}/spreadsheets/${this.spreadSheetUniqueTitle}/workflow-overlaps`,
          { headers: this.headers() }
        )
        this.overlaps = overlaps.data || {}
      } catch (error) {
        const message = error && error.response && error.response.data
        this.saveError = typeof message === 'string' && message ? message : 'Could not save hooks.'
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.hook-note,
.hook-empty,
.hook-hint,
.hook-latest {
  color: #666;
  font-size: 0.85rem;
}
.hook-note,
.hook-empty {
  margin-bottom: 0.6rem;
  max-width: 42rem;
}
.hook-card {
  border: 1px solid #e4e0d8;
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
}
.hook-row,
.hook-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}
.hook-input {
  height: 30px;
  width: 14rem;
  max-width: 100%;
  font-size: 0.85rem;
}
.hook-input-wide {
  width: 22rem;
}
.hook-fields {
  display: block;
  width: 16rem;
  max-width: 100%;
  height: 5.5rem;
  font-size: 0.85rem;
  margin-top: 0.25rem;
}
.hook-label {
  font-size: 0.85rem;
}
.hook-block {
  margin: 0.4rem 0;
}
.hook-remove,
.hook-add,
.hook-save {
  height: 30px;
  font-size: 0.85rem;
  padding: 0 0.75rem;
}
.hook-remove {
  color: #9d4b6a;
  background: transparent;
  border: 1px solid #e7c5d1;
}
.hook-add,
.hook-save {
  background: #1a1a17;
  color: #fff;
  border: 1px solid #1a1a17;
}
.hook-add:disabled,
.hook-save:disabled {
  opacity: 0.5;
}
.hook-saved {
  color: #2f6b3a;
  font-size: 0.85rem;
}
.hook-error,
.hook-warning {
  color: #8a5a12;
  font-size: 0.85rem;
}
.hook-warning {
  margin: 0.4rem 0 0;
}
</style>
